// Class divergence between a season and the club fleet config (ra-gbrd.11).
//
// Classes live in TWO disconnected Firestore places that the app and the
// committee/schedule each read independently:
//
//   season.classes  (clubs/{id}/events/{seasonId}.classes)
//     — what the APP reads to offer boat registration ({ id, name }).
//   fleet_config.classes  (clubs/{id}/fleet_config/current.classes)
//     — what the committee race-day + published race schedule FK to, by id
//       (adds flag codes, fleets, start locations, start order).
//
// Nothing propagates fleet_config -> season.classes ("Sync class order" only
// reorders classes that already exist in both). So the two sets silently drift:
//
//   - A class only in the SEASON is offered for registration but has no racing
//     details / start in fleet_config — registered boats match NO start.
//   - A class only in FLEET CONFIG is referenced by the schedule but is never
//     offered for registration — nobody can register against that start.
//
// Matching is by EXACT class id, because that is what the app and schedule use
// to link the two. A case-only difference ("J109" vs "j109") therefore reads as
// real divergence — which it is, since the FK would not resolve at runtime.

/** Normalise a class list to plain { id, name } objects, dropping blanks. */
function normalize(classes) {
  return (classes || [])
    .filter((c) => c && c.id)
    .map((c) => ({ id: c.id, name: c.name || c.id }));
}

/**
 * Compare a season's classes against the club fleet config.
 *
 * @param {Array<{id:string,name?:string}>} seasonClasses - season.classes (app registration).
 * @param {Array<{id:string,name?:string}>} fleetClasses  - fleet_config.classes (schedule FK).
 * @returns {{
 *   onlyInSeason: Array<{id:string,name:string}>,  // offered for registration, no racing details/start
 *   onlyInFleet:  Array<{id:string,name:string}>,  // referenced by schedule, not offered for registration
 *   matched:      Array<{id:string,name:string}>,  // linked in both
 *   inSync: boolean,                               // true iff neither side has extras
 * }}
 */
export function diffSeasonFleetClasses(seasonClasses, fleetClasses) {
  const season = normalize(seasonClasses);
  const fleet = normalize(fleetClasses);
  const seasonIds = new Set(season.map((c) => c.id));
  const fleetIds = new Set(fleet.map((c) => c.id));

  const onlyInSeason = season.filter((c) => !fleetIds.has(c.id));
  const onlyInFleet = fleet.filter((c) => !seasonIds.has(c.id));
  const matched = season.filter((c) => fleetIds.has(c.id));

  return {
    onlyInSeason,
    onlyInFleet,
    matched,
    inSync: onlyInSeason.length === 0 && onlyInFleet.length === 0,
  };
}

/**
 * Merge `additions` into `target`, appending any whose id is not already
 * present. Existing entries keep their position and fields; this never mutates
 * or reorders the originals, so it is safe to apply repeatedly (idempotent).
 *
 * @param {Array<{id:string}>} target
 * @param {Array<{id:string,name?:string}>} additions - extra classes to fold in (id + name only).
 * @returns {{ classes: Array, added: Array }} the merged list and the entries actually appended.
 */
export function mergeClassesById(target, additions) {
  const existing = new Set((target || []).map((c) => c.id));
  const added = [];
  for (const c of normalize(additions)) {
    if (existing.has(c.id)) continue;
    existing.add(c.id);
    added.push({ id: c.id, name: c.name });
  }
  return { classes: [...(target || []), ...added], added };
}
